import React, { useCallback, useRef } from 'react';
import { IconButton } from 'components/buttons/IconButton';
import { SettingsColumn } from '../columns';
import './AddOutput.less';
import { HasOpenState } from 'hooks/HasOpenState';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { IActionLinkItems } from 'components/ActionLinks/ActionLinkModels';
import { getHeaderActionsMenu } from '../TruthTableUtils';
import { IRootGroupModel } from 'domains/designers/RuleDesigner/models/RootGroupModel';

interface IAddOutputProps extends Omit<React.HTMLProps<HTMLTableCellElement>, 'onClick'> {
  rootModel: IRootGroupModel;
  onAddOutput: () => void;
  onAddHeaderAction: (actionType: string) => void;
}

/**
 * Add output cell.
 * It is supposed to be used in Truth Table Header to add a column.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function AddOutput({ rootModel, className, onAddOutput, onAddHeaderAction, ...otherProps }: IAddOutputProps) {
  const { allowActions: hasActions } = rootModel;
  const { isOpen, setOpen, setClosed } = HasOpenState();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleOnAdd = useCallback(() => {
    if (hasActions) {
      setOpen();
    } else {
      onAddOutput();
    }
  }, [hasActions, onAddOutput, setOpen]);

  const convertToActionLink = (element: HTMLElement, rootModel: IRootGroupModel) => {
    const menu: IActionLinkItems =
      element != null ? getHeaderActionsMenu(rootModel.getActionTypes(element)) : { current: [] };
    if (rootModel.canAddOutputData) {
      menu.current!.unshift({
        value: 'New Output Data',
        label: 'New Output Data',
        onClick: () => {
          onAddOutput();
          setClosed();
        },
      });
    }

    return menu;
  };

  return (
    <SettingsColumn {...otherProps} isHeader>
      {(rootModel.canAddOutputData || hasActions) && (
        <IconButton
          ref={buttonRef}
          title="Add Output"
          aria-label="Add Output"
          className="tt-cell full-size tt-table__add-output-btn"
          iconClassName="tt-table__add-output-icon"
          onClick={handleOnAdd}
        />
      )}
      {hasActions && (
        <Callout
          isOpen={isOpen}
          onClose={setClosed}
          sourceRef={buttonRef}
          placement="right"
          showArrow={true}
          flipBehavior="flip"
        >
          <ActionLinks
            values={convertToActionLink(buttonRef.current!, rootModel)}
            callback={actionTypeName => {
              onAddHeaderAction(actionTypeName);
              setClosed();
            }}
          />
        </Callout>
      )}
    </SettingsColumn>
  );
}
